import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Bookmark, Trash, PencilSimple, Plus, X, Lightning, Briefcase, Folder, ClipboardText, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const TEMPLATE_TYPES = [
  { value: "request", label: "Request", icon: ClipboardText, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  { value: "job", label: "Job", icon: Briefcase, color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  { value: "project", label: "Project", icon: Folder, color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" },
  { value: "emergency", label: "Emergency Job", icon: Lightning, color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
];

const CATEGORIES = ["general", "plumbing", "electrical", "carpentry", "painting", "roofing", "landscaping", "cleaning", "hvac", "fire_safety", "security", "mould_eradication", "other"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const URGENCIES = ["urgent", "critical", "life_threatening"];

const BLANK_FIELDS = {
  request: { title: "", category: "general", priority: "medium", property_address: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", request_details: "" },
  job: { title: "", category: "general", property_name: "", street_address: "", town_city: "", county: "", postcode: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", instructions: "" },
  project: { name: "", project_type: "maintenance", priority: "medium", description: "", safety_notes: "" },
  emergency: { title: "", description: "", urgency: "urgent", category: "general", property_name: "", street_address: "", town_city: "", county: "", postcode: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", instructions: "" },
};

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", template_type: "job", fields: { ...BLANK_FIELDS.job } });

  const fetchTemplates = useCallback(async () => {
    try {
      const params = filterType !== "all" ? `?template_type=${filterType}` : "";
      const { data } = await API.get(`/work-templates${params}`);
      setTemplates(data.templates || []);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openCreate = () => {
    const ttype = filterType !== "all" ? filterType : "job";
    setForm({ name: "", description: "", template_type: ttype, fields: { ...BLANK_FIELDS[ttype] } });
    setShowCreate(true);
  };

  const changeType = (ttype) => {
    setForm(prev => ({ ...prev, template_type: ttype, fields: { ...BLANK_FIELDS[ttype] } }));
  };

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, fields: { ...prev.fields, [key]: value } }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Template name is required"); return; }
    try {
      await API.post("/work-templates", { name: form.name, description: form.description, template_type: form.template_type, fields: form.fields });
      toast.success("Template created");
      setShowCreate(false);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
  };

  const openEdit = (t) => {
    setEditTemplate(t);
    setForm({ name: t.name, description: t.description || "", template_type: t.template_type, fields: { ...BLANK_FIELDS[t.template_type], ...t.fields } });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/work-templates/${editTemplate.id}`, { name: form.name, description: form.description, fields: form.fields });
      toast.success("Template updated");
      setEditTemplate(null);
      fetchTemplates();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await API.delete(`/work-templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const typeInfo = (value) => TEMPLATE_TYPES.find(t => t.value === value) || TEMPLATE_TYPES[1];

  const filtered = filterType === "all" ? templates : templates.filter(t => t.template_type === filterType);

  const renderFields = (isCreate) => {
    const f = form.fields;
    const ttype = form.template_type;

    if (ttype === "request") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Default Title</Label><Input value={f.title || ""} onChange={e => updateField("title", e.target.value)} placeholder="E.g. Boiler Repair" className="mt-1" data-testid="tpl-title" /></div>
          <div><Label>Category</Label>
            <Select value={f.category || "general"} onValueChange={v => updateField("category", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Priority</Label>
            <Select value={f.priority || "medium"} onValueChange={v => updateField("priority", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Default Property Address</Label><Input value={f.property_address || ""} onChange={e => updateField("property_address", e.target.value)} placeholder="Optional default address" className="mt-1" /></div>
        </div>
        <p className="text-xs font-semibold text-muted-foreground">SITE / PROPERTY CONTACT</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Site Contact</Label><Input value={f.site_contact_name || ""} onChange={e => updateField("site_contact_name", e.target.value)} placeholder="E.G Tenant" className="mt-1" /></div>
          <div><Label>Phone</Label><Input value={f.site_contact_phone || ""} onChange={e => updateField("site_contact_phone", e.target.value)} placeholder="+44 7..." className="mt-1" /></div>
          <div><Label>Email</Label><Input value={f.site_contact_email || ""} onChange={e => updateField("site_contact_email", e.target.value)} placeholder="tenant@email.com" className="mt-1" /></div>
        </div>
        <div><Label>Default Request Details</Label><Textarea value={f.request_details || ""} onChange={e => updateField("request_details", e.target.value)} rows={3} placeholder="Pre-filled instructions or checklist..." className="mt-1" /></div>
      </div>
    );

    if (ttype === "job") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Default Job Title</Label><Input value={f.title || ""} onChange={e => updateField("title", e.target.value)} placeholder="E.g. Annual Gas Safety Check" className="mt-1" data-testid="tpl-title" /></div>
          <div><Label>Category</Label>
            <Select value={f.category || "general"} onValueChange={v => updateField("category", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs font-semibold text-muted-foreground">DEFAULT PROPERTY DETAILS</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Property Name</Label><Input value={f.property_name || ""} onChange={e => updateField("property_name", e.target.value)} placeholder="Optional" className="mt-1" /></div>
          <div className="col-span-2"><Label>Street Address</Label><Input value={f.street_address || ""} onChange={e => updateField("street_address", e.target.value)} className="mt-1" /></div>
          <div><Label>Town / City</Label><Input value={f.town_city || ""} onChange={e => updateField("town_city", e.target.value)} className="mt-1" /></div>
          <div><Label>Postcode</Label><Input value={f.postcode || ""} onChange={e => updateField("postcode", e.target.value)} className="mt-1" /></div>
        </div>
        <p className="text-xs font-semibold text-muted-foreground">SITE / PROPERTY CONTACT</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Site Contact</Label><Input value={f.site_contact_name || ""} onChange={e => updateField("site_contact_name", e.target.value)} placeholder="E.G Tenant" className="mt-1" /></div>
          <div><Label>Phone</Label><Input value={f.site_contact_phone || ""} onChange={e => updateField("site_contact_phone", e.target.value)} placeholder="+44 7..." className="mt-1" /></div>
          <div><Label>Email</Label><Input value={f.site_contact_email || ""} onChange={e => updateField("site_contact_email", e.target.value)} placeholder="tenant@email.com" className="mt-1" /></div>
        </div>
        <div><Label>Default Instructions</Label><Textarea value={f.instructions || ""} onChange={e => updateField("instructions", e.target.value)} rows={3} placeholder="Standard instructions for this job type..." className="mt-1" /></div>
      </div>
    );

    if (ttype === "project") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Default Project Name</Label><Input value={f.name || ""} onChange={e => updateField("name", e.target.value)} placeholder="E.g. Bathroom Refurbishment" className="mt-1" data-testid="tpl-title" /></div>
          <div><Label>Project Type</Label>
            <Select value={f.project_type || "maintenance"} onValueChange={v => updateField("project_type", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["maintenance", "refurbishment", "new_build", "renovation", "fit_out", "planned_works", "reactive_repair", "inspection", "other"].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Priority</Label>
            <Select value={f.priority || "medium"} onValueChange={v => updateField("priority", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Description</Label><Textarea value={f.description || ""} onChange={e => updateField("description", e.target.value)} rows={3} placeholder="Standard project scope description..." className="mt-1" /></div>
        <div><Label>Safety Notes</Label><Textarea value={f.safety_notes || ""} onChange={e => updateField("safety_notes", e.target.value)} rows={2} placeholder="Default safety requirements..." className="mt-1" /></div>
      </div>
    );

    if (ttype === "emergency") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Default Title</Label><Input value={f.title || ""} onChange={e => updateField("title", e.target.value)} placeholder="E.g. Burst Pipe Emergency" className="mt-1" data-testid="tpl-title" /></div>
          <div><Label>Urgency</Label>
            <Select value={f.urgency || "urgent"} onValueChange={v => updateField("urgency", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{URGENCIES.map(u => <SelectItem key={u} value={u}>{u.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Category</Label>
            <Select value={f.category || "general"} onValueChange={v => updateField("category", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Default Description</Label><Textarea value={f.description || ""} onChange={e => updateField("description", e.target.value)} rows={2} placeholder="What the emergency involves..." className="mt-1" /></div>
        <p className="text-xs font-semibold text-muted-foreground">DEFAULT PROPERTY DETAILS</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Property Name</Label><Input value={f.property_name || ""} onChange={e => updateField("property_name", e.target.value)} placeholder="Optional" className="mt-1" /></div>
          <div className="col-span-2"><Label>Street Address</Label><Input value={f.street_address || ""} onChange={e => updateField("street_address", e.target.value)} className="mt-1" /></div>
          <div><Label>Town / City</Label><Input value={f.town_city || ""} onChange={e => updateField("town_city", e.target.value)} className="mt-1" /></div>
          <div><Label>Postcode</Label><Input value={f.postcode || ""} onChange={e => updateField("postcode", e.target.value)} className="mt-1" /></div>
        </div>
        <p className="text-xs font-semibold text-muted-foreground">SITE / PROPERTY CONTACT</p>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Site Contact</Label><Input value={f.site_contact_name || ""} onChange={e => updateField("site_contact_name", e.target.value)} placeholder="E.G Tenant" className="mt-1" /></div>
          <div><Label>Phone</Label><Input value={f.site_contact_phone || ""} onChange={e => updateField("site_contact_phone", e.target.value)} placeholder="+44 7..." className="mt-1" /></div>
          <div><Label>Email</Label><Input value={f.site_contact_email || ""} onChange={e => updateField("site_contact_email", e.target.value)} placeholder="tenant@email.com" className="mt-1" /></div>
        </div>
        <div><Label>Default Instructions</Label><Textarea value={f.instructions || ""} onChange={e => updateField("instructions", e.target.value)} rows={3} placeholder="Standard emergency procedures..." className="mt-1" /></div>
      </div>
    );

    return null;
  };

  const fieldSummary = (t) => {
    const f = t.fields || {};
    const filled = Object.entries(f).filter(([, v]) => v && v !== "general" && v !== "medium" && v !== "urgent" && v !== "maintenance");
    return filled.length;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Work Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Reusable templates for requests, jobs, projects and emergencies</p>
        </div>
        <Button onClick={openCreate} data-testid="create-template-btn"><Plus size={16} className="mr-1" /> New Template</Button>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 mb-5 flex-wrap" data-testid="template-type-filter">
        <Button size="sm" variant={filterType === "all" ? "default" : "outline"} className="text-xs h-7" onClick={() => setFilterType("all")} data-testid="filter-all">All ({templates.length})</Button>
        {TEMPLATE_TYPES.map(tt => {
          const count = templates.filter(t => t.template_type === tt.value).length;
          const Icon = tt.icon;
          return (
            <Button key={tt.value} size="sm" variant={filterType === tt.value ? "default" : "outline"} className="text-xs h-7" onClick={() => setFilterType(tt.value)} data-testid={`filter-${tt.value}`}>
              <Icon size={12} className="mr-1" />{tt.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Template cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <Bookmark size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No templates yet</p>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Create a template to pre-fill forms when creating new requests, jobs, projects or emergencies.</p>
          <Button onClick={openCreate}><Plus size={16} className="mr-1" /> Create Template</Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map(t => {
            const ti = typeInfo(t.template_type);
            const Icon = ti.icon;
            const filledCount = fieldSummary(t);
            return (
              <div key={t.id} className="border border-border rounded-md p-4 hover:border-primary/30 transition-all" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[9px] gap-1 ${ti.color}`}><Icon size={10} />{ti.label}</Badge>
                    </div>
                    <p className="font-semibold truncate">{t.name}</p>
                  </div>
                  <div className="flex gap-0.5 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)} data-testid={`edit-tpl-${t.id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDelete(t.id)} data-testid={`delete-tpl-${t.id}`}><Trash size={14} /></Button>
                  </div>
                </div>
                {t.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{t.description}</p>}
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span>{filledCount} pre-filled field{filledCount !== 1 ? "s" : ""}</span>
                  <span>by {t.created_by_name}</span>
                </div>
                {/* Show key pre-filled values */}
                {t.fields && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.fields.title && <span className="text-[10px] px-2 py-0.5 bg-primary/10 text-primary rounded truncate max-w-[120px]">{t.fields.title}</span>}
                    {t.fields.name && <span className="text-[10px] px-2 py-0.5 bg-primary/10 text-primary rounded truncate max-w-[120px]">{t.fields.name}</span>}
                    {t.fields.category && t.fields.category !== "general" && <span className="text-[10px] px-2 py-0.5 bg-muted text-muted-foreground rounded capitalize">{t.fields.category}</span>}
                    {t.fields.priority && t.fields.priority !== "medium" && <span className="text-[10px] px-2 py-0.5 bg-muted text-muted-foreground rounded capitalize">{t.fields.priority}</span>}
                    {t.fields.urgency && t.fields.urgency !== "urgent" && <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded capitalize">{t.fields.urgency}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showCreate || !!editTemplate} onOpenChange={(v) => { if (!v) { setShowCreate(false); setEditTemplate(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editTemplate ? "Edit Template" : "Create New Template"}</DialogTitle>
            <DialogDescription>{editTemplate ? "Update the template fields." : "Choose a type and fill in the default values for the template."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={editTemplate ? handleSave : handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Template Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="E.g. Standard Boiler Service" className="mt-1" data-testid="tpl-name" /></div>
              {!editTemplate && (
                <div className="col-span-2">
                  <Label>Template Type *</Label>
                  <div className="flex gap-2 mt-1.5">
                    {TEMPLATE_TYPES.map(tt => {
                      const Icon = tt.icon;
                      return (
                        <button key={tt.value} type="button" onClick={() => changeType(tt.value)}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border transition-all ${form.template_type === tt.value ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
                          data-testid={`type-${tt.value}`}
                        >
                          <Icon size={14} />{tt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {editTemplate && (
                <div className="col-span-2">
                  <Label>Template Type</Label>
                  <div className="mt-1"><Badge className={typeInfo(form.template_type).color}>{typeInfo(form.template_type).label}</Badge></div>
                </div>
              )}
              <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="When should this template be used..." className="mt-1" /></div>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-xs font-semibold text-muted-foreground mb-3">DEFAULT FIELD VALUES</p>
              {renderFields()}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreate(false); setEditTemplate(null); }}>Cancel</Button>
              <Button type="submit" data-testid="save-template-btn">{editTemplate ? "Save Changes" : "Create Template"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
